import {
  Checkbox,
  Input,
  NumberInput,
  Radio,
  Select,
  Stack,
  TextInput,
  Textarea,
  ActionIcon,
  Text,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";
import { IconAt, IconFileUpload, IconTrash, IconFileTypePdf, IconFileTypeJpg } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormField } from "~/types/form-field";
import { IMAGE_FILE_TYPES, DOCUMENT_FILE_TYPES, MEGABYTE, MAX_FILENAME_LENGTH_BEFORE_UPLOAD } from "~/constants";
import { truncateWithEllipsis } from "~/utils/truncate-text";
import classes from "../../routes/($lang).doctor.$slug/book-doctor-detail.module.scss";

interface DynamicFormFieldsProps {
  fields: FormField[];
  form: UseFormReturnType<any>;
  t: any;
}

const FileUploadField = ({
  field,
  form,
  t,
}: {
  field: FormField;
  form: UseFormReturnType<any>;
  t: any;
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const maxFiles = field.fileOptions?.maxFiles ?? 10;
      const maxSize = (field.fileOptions?.maxSize ?? 10) * MEGABYTE;

      const validFiles = acceptedFiles.filter(file => {
        if (file.size > maxSize) return false;
        return true;
      });

      const currentFiles = form.values.files || [];
      const newFiles = [...currentFiles, ...validFiles].slice(0, maxFiles);
      form.setFieldValue("files", newFiles);
    },
    [form, field.fileOptions]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: field.fileOptions?.accept
      ? {
          "image/*": IMAGE_FILE_TYPES,
          "application/pdf": DOCUMENT_FILE_TYPES,
        }
      : undefined,
    onDrop,
    multiple: field.fileOptions?.multiple ?? true,
  });

  const removeFile = (file: File) => {
    const currentFiles = form.values.files || [];
    const newFiles = currentFiles.filter((f: File) => f !== file);
    form.setFieldValue("files", newFiles);
  };

  const uploadedFiles = form.values.files || [];

  return (
    <Stack key={field.id} gap="0.5rem">
      <Input.Label required={field.required}>{field.label}</Input.Label>
      <div className={classes["files-wrapper"]}>
        <div
          {...getRootProps({
            className: `${classes.dropzone} ${isDragActive ? classes.active : ""} ${
              isDragAccept ? classes.accept : ""
            } ${isDragReject ? classes.reject : ""}`,
          })}>
          <input {...getInputProps()} name={field.id} />
          <Stack align="center">
            <div className={classes["icon-wrapper"]}>
              <IconFileUpload color="white" />
            </div>
            <Stack gap="0.5rem" ta="center">
              <Text fz={14} fw={500}>{t("drag-file-here")}</Text>
              <Text fz={13} c="dimmed">{t("drag-file-here-continuation")}</Text>
            </Stack>
          </Stack>
        </div>
        {uploadedFiles.length > 0 && (
          <div className={classes["uploaded-files"]}>
            <Title order={4} fw={500} fz={15}>{t("uploaded-files")}</Title>
            <ul>
              {uploadedFiles.map((file: File) => (
                <li key={file.name}>
                  <div className={classes.content}>
                    {file.type === "application/pdf" ? <IconFileTypePdf /> : <IconFileTypeJpg />}
                    <p>{truncateWithEllipsis(file.name, MAX_FILENAME_LENGTH_BEFORE_UPLOAD)} ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})</p>
                  </div>
                  <ActionIcon color="red" variant="subtle" onClick={() => removeFile(file)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Stack>
  );
};

export const DynamicFormFields = ({ fields, form, t }: DynamicFormFieldsProps) => {
  const renderField = (field: FormField) => {
    // Dynamic fields are stored in form.values.additionalData[field.id]
    const fieldPath = `additionalData.${field.id}`;
    
    switch (field.inputType) {
      case "text":
        return (
          <TextInput
            key={field.id}
            label={field.label}
            placeholder={field.placeholder}
            required={field.required}
            {...form.getInputProps(fieldPath)}
          />
        );
      case "email":
        return (
          <TextInput
            key={field.id}
            label={field.label}
            type="email"
            leftSection={<IconAt size={16} stroke={1.5} />}
            required={field.required}
            placeholder={field.placeholder}
            {...form.getInputProps(fieldPath)}
          />
        );
      case "radio":
        return (
          <Radio.Group
            key={field.id}
            label={field.label}
            required={field.required}
            {...form.getInputProps(fieldPath)}>
            <Stack gap="xs">
              {field.options?.map(option => (
                <Radio key={option.value} value={option.value} label={option.label} mt="0.25rem" />
              ))}
            </Stack>
          </Radio.Group>
        );
      case "number":
        return (
          <NumberInput
            key={field.id}
            label={field.label}
            placeholder={field.placeholder}
            required={field.required}
            min={0}
            {...form.getInputProps(fieldPath)}
          />
        );
      case "checkbox":
        return (
          <Checkbox.Group
            key={field.id}
            label={field.label}
            required={field.required}
            {...form.getInputProps(fieldPath)}>
            <Stack gap="xs">
              {field.options?.map(option => (
                <Checkbox key={option.value} value={option.value} label={option.label} mt="0.25rem" />
              ))}
            </Stack>
          </Checkbox.Group>
        );
      case "textarea":
        return (
          <Textarea
            key={field.id}
            label={field.label}
            rows={5}
            resize="vertical"
            placeholder={field.placeholder}
            required={field.required}
            {...form.getInputProps(fieldPath)}
          />
        );
      case "date":
        return (
          <DatePickerInput
            key={field.id}
            label={field.label}
            placeholder={field.placeholder}
            required={field.required}
            {...form.getInputProps(fieldPath)}
          />
        );
      case "select":
        return (
          <Select
            key={field.id}
            label={field.label}
            placeholder={field.placeholder}
            required={field.required}
            data={field.options}
            {...form.getInputProps(fieldPath)}
          />
        );
      case "file":
        return <FileUploadField key={field.id} field={field} form={form} t={t} />;
      default:
        return null;
    }
  };

  return (
    <Stack gap="xs" mt="1rem">
      {fields.map(renderField)}
    </Stack>
  );
};
